import {
  LOGIC_ELIGIBLE_READ_KEYS,
  LOGIC_ELIGIBLE_READ_PREFIXES,
  type Condition,
  type FormDefinition,
  type FormItem,
  type FormSection,
  type ItemType,
  type TypedAnswer,
} from "./types";

/**
 * Whether a condition question names a read subject — a
 * `LOGIC_ELIGIBLE_READ_KEYS` key or a non-empty key under one of the
 * `LOGIC_ELIGIBLE_READ_PREFIXES` — rather than an item: link_ids carry no dot,
 * so the two never collide. Twin of Go `logicEligibleReadKey`.
 */
export function isReadSubject(question: string): boolean {
  return (
    Object.hasOwn(LOGIC_ELIGIBLE_READ_KEYS, question) ||
    LOGIC_ELIGIBLE_READ_PREFIXES.some(
      (prefix) => question.startsWith(prefix) && question.length > prefix.length,
    )
  );
}

/**
 * The read subjects a definition's conditions name: every condition question
 * `isReadSubject` holds for, on every section's and item's `enable_when` at
 * every depth (repeating sections included), every `copy_from.when`, and every
 * outcome group's `applies_when` and `criteria`; deduplicated, in ascending
 * order. The seal writes `READS_MEMBER` for exactly these keys and the receipt
 * holds the member to them. Twin of Go `ReferencedReadSubjects`.
 */
export function readSubjectsOf(definition: FormDefinition): string[] {
  const named = new Set<string>();
  const collect = (conditions: readonly Condition[] | undefined): void => {
    for (const c of conditions ?? []) if (isReadSubject(c.question)) named.add(c.question);
  };
  const walk = (section: FormSection): void => {
    collect(section.enable_when);
    for (const item of section.items) {
      collect(item.enable_when);
      collect(item.copy_from?.when);
    }
    for (const sub of section.sections ?? []) walk(sub);
  };
  for (const section of definition.sections) walk(section);
  for (const group of definition.computed?.outcome?.groups ?? []) {
    collect(group.applies_when);
    collect(group.criteria);
  }
  return [...named].sort();
}

const OPERAND_MEMBERS: readonly (keyof TypedAnswer)[] = [
  "pass_fail",
  "boolean",
  "number",
  "text",
  "option",
  "date",
  "datetime",
];

// The item type a subject reads as, by its condition's operand member;
// `pass_fail` has none.
const SUBJECT_OPERAND_TYPES: Partial<Record<keyof TypedAnswer, ItemType>> = {
  option: "select_one",
  number: "number",
  date: "date",
  datetime: "datetime",
  boolean: "boolean",
  text: "text",
};

/**
 * The synthetic display item a condition naming a read subject evaluates: the
 * subject as link_id and read_source, typed by the condition's single
 * populated operand member (no answer, `pass_fail` or several members read as
 * text, so `exists` is non-empty presence). The publish gate holds that member
 * to the subject's logic type, so a published condition reads the subject as
 * its own type. Its link_id carries a dot, so no enabled entry ever masks it.
 * Twin of Go `subjectItem`; exported for the package's sibling modules, not on
 * the barrel.
 */
export function subjectItem(c: Condition): FormItem {
  // A stored definition is untyped JSON: an answer or a member stored as null
  // reads as absent, as the Go twin decodes it to a nil pointer.
  const answer: TypedAnswer | null | undefined = c.answer;
  const populated = OPERAND_MEMBERS.filter(
    (member) => answer?.[member] !== undefined && answer[member] !== null,
  );
  const [only] = populated;
  const type =
    (populated.length === 1 && only !== undefined ? SUBJECT_OPERAND_TYPES[only] : undefined) ??
    "text";
  return { link_id: c.question, type, read_source: c.question, read_mode: "display" };
}
